/**
 * TECHNOVARD – recently viewed products (sections/tv-recently-viewed.liquid).
 *
 * Remembers the ids of the products a shopper opens, in their own browser only (localStorage),
 * and asks Shopify to render the cards for them through the search page with the Section
 * Rendering API. Nothing is fetched until the section is close to the viewport, and the section
 * stays hidden when storage is unavailable, the request fails or no product comes back.
 */
(() => {
  'use strict';

  if (customElements.get('tv-recently-viewed')) return;

  const STORAGE_KEY = 'tv-recently-viewed';
  const MAX_STORED = 12;
  const isId = (id) => /^\d+$/.test(id);

  const readIds = () => {
    try {
      const ids = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(ids) ? ids.map(String).filter(isId) : [];
    } catch {
      return [];
    }
  };

  const writeIds = (ids) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(0, MAX_STORED)));
    } catch {
      // Private mode or storage disabled: the section simply stays hidden.
    }
  };

  class TvRecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;

      const current = String(this.dataset.productId || '');
      const limit = parseInt(this.dataset.limit, 10) || 4;
      const previous = readIds().filter((id) => id !== current);
      if (isId(current)) writeIds([current, ...previous]);

      this.ids = previous.slice(0, limit);
      if (!this.ids.length) return;

      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      // The element itself is hidden, so watch its (always rendered) parent.
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 1200px 0px' }
      );
      this.observer.observe(this.parentElement || this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    load() {
      const url = new URL(this.dataset.url || '/search', window.location.origin);
      url.searchParams.set('q', this.ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('section_id', 'tv-recently-viewed');

      fetch(url.toString())
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(response.status))))
        .then((html) => this.render(html))
        .catch(() => {});
    }

    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fragment = doc.querySelector('[data-tv-viewed-cards]');
      if (!fragment) return;
      const cards = new Map();
      fragment.querySelectorAll(':scope > [data-product-id]').forEach((item) => {
        cards.set(item.dataset.productId, item);
      });

      // Products that no longer come back (deleted, unpublished) are dropped from storage.
      const missing = this.ids.filter((id) => !cards.has(id));
      if (missing.length) writeIds(readIds().filter((id) => !missing.includes(id)));

      const list = this.querySelector('[data-tv-viewed-list]');
      const items = this.ids.filter((id) => cards.has(id)).map((id) => document.importNode(cards.get(id), true));
      if (!list || !items.length) return;

      list.replaceChildren(...items);
      this.hidden = false;
    }
  }

  customElements.define('tv-recently-viewed', TvRecentlyViewed);
})();
